import React from "react";
import { LoginForm } from "./login-form";
import { Logo } from "@/components/ui/logo";
import { Footer } from "./Footer";
import { Brain, Zap, Shield } from "lucide-react";
import { SiteHeader } from "./SiteHeader";
interface LoginProps {
  pendingAuth?: string | null;
}

export const Login: React.FC<LoginProps> = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100/50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 flex flex-col">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]" />

      <SiteHeader className="sticky top-0 z-10" />

      {/* Main Container - Centered Single Column */}
      <div className="relative flex flex-1 flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Hero Section */}
          <div className="text-center mb-12 animate-in fade-in-50 slide-in-from-top-4 duration-700">
            {/* Primary Logo */}
            <div className="mb-8">
              <Logo
                className="h-16 w-auto max-w-[320px] mx-auto"
                onClick={() => (window.location.href = "https://forgecode.dev")}
              />
            </div>

            {/* Hero Content */}
            <div className="space-y-4">
              <div className="space-y-2">
                <h1 className="text-4xl font-semibold tracking-tight text-slate-900 dark:text-white leading-tight">
                  AI Pair Programmer
                </h1>
                <h1 className="text-4xl font-semibold tracking-tight text-slate-900 dark:text-white leading-tight">
                  in Your Terminal
                </h1>
              </div>
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed font-medium max-w-sm mx-auto">
                Code • Refactor • Debug • Test • Documentation
              </p>
            </div>
          </div>
          {/* Primary Focus - Sign-in Card */}
          <div className="relative mb-8 animate-in fade-in-50 slide-in-from-bottom-4 duration-700 delay-200">
            {/* Enhanced glow effect for focus */}
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/15 via-indigo-500/15 to-purple-500/15 rounded-3xl blur-2xl opacity-60 animate-pulse"></div>

            {/* Main form container - Hero element */}
            <div className="relative bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-2xl shadow-slate-900/10 dark:shadow-black/20">
              <LoginForm className="p-10" />
            </div>
          </div>

          {/* Trust Indicators - Subtle and supportive */}
          <div className="text-center space-y-6 animate-in fade-in-50 slide-in-from-bottom-4 duration-700 delay-400">
            {/* Feature highlights - horizontal layout for better balance */}
            <div className="flex items-center justify-center space-x-8">
              {[
                { icon: Brain, text: "Intelligent assistance" },
                { icon: Zap, text: "Terminal integration" },
                { icon: Shield, text: "Privacy-focused" },
              ].map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex flex-col items-center space-y-2 group"
                  >
                    <div className="p-2 rounded-lg bg-slate-100/50 dark:bg-slate-800/50 group-hover:bg-slate-200/50 dark:group-hover:bg-slate-700/50 transition-colors duration-200">
                      <IconComponent className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      {feature.text}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="pt-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                By signing in, you agree to our{" "}
                <a
                  href="https://forgecode.dev/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
